// Generate/retrieve a stable anonymous device id kept in localStorage.
// Used as X-Device-Id header on all API calls to keep each device's history private.

const KEY = "soniclab_device_id";

function makeId() {
  // Prefer crypto.randomUUID when available
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `dev-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export function getDeviceId() {
  try {
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = makeId();
      localStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    // localStorage disabled → fall back to session-scoped id (still >= 8 chars for server)
    return "session-fallback-id";
  }
}

const ALERT_KEY = "soniclab_privacy_alert_dismissed";

export function isPrivacyAlertDismissed() {
  try {
    return localStorage.getItem(ALERT_KEY) === "1";
  } catch {
    return false;
  }
}

export function dismissPrivacyAlert() {
  try {
    localStorage.setItem(ALERT_KEY, "1");
  } catch {
    /* ignore */
  }
}

// ---- Admin bypass ----------------------------------------------------------
// Detect `?admin=<token>` in the URL on load and persist it in localStorage.
// Once set, all API calls include it and the monthly quota is bypassed.
const ADMIN_KEY = "spectraloom_admin_token";

function readAdminFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("admin");
    if (t) {
      localStorage.setItem(ADMIN_KEY, t);
      return t;
    }
  } catch {
    /* ignore */
  }
  return null;
}

// Run once at module load so ?admin=... anywhere in the app persists.
readAdminFromUrl();

export function getAdminToken() {
  try {
    return localStorage.getItem(ADMIN_KEY) || readAdminFromUrl();
  } catch {
    return null;
  }
}

export function clearAdminToken() {
  try {
    localStorage.removeItem(ADMIN_KEY);
  } catch {
    /* ignore */
  }
}

// ---- Copyright confirmation caching ---------------------------------------
// We keep the very last "I own the rights" acknowledgement timestamp locally
// so that a user uploading multiple tracks within a short window (5 minutes)
// gets a lightweight one-click re-confirmation instead of the full modal.
// Legal proof is unaffected: the ToS/Privacy consent is still stored on
// MongoDB and every upload still requires an explicit click.
const COPYRIGHT_TS_KEY = "spectraloom_copyright_confirmed_at";
export const COPYRIGHT_QUICK_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

export function getLastCopyrightConfirmedAt() {
  try {
    const v = localStorage.getItem(COPYRIGHT_TS_KEY);
    return v ? parseInt(v, 10) : 0;
  } catch {
    return 0;
  }
}

export function markCopyrightConfirmed() {
  try {
    localStorage.setItem(COPYRIGHT_TS_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function isCopyrightConfirmationFresh() {
  const ts = getLastCopyrightConfirmedAt();
  return ts > 0 && Date.now() - ts < COPYRIGHT_QUICK_WINDOW_MS;
}
